import React from "react";
import { useTranslation } from "react-i18next";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAdminRevenueHotels } from "../hooks/useAdminRevenueHotels";
import { useAdminTenantTableExport } from "../hooks/useAdminTenantTableExport";
import { PartnerRevenueLegendSection } from "./PartnerRevenueLegendSection";
import { RevenueDistributionSection } from "./RevenueDistributionSection";
import { AdminRevenueHotelsTable } from "./revenue/AdminRevenueHotelsTable";

export const AdminRevenueTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const {
    tenantId,
    tableRows,
    revenueDistribution,
    totalRevenue,
    totalRevenueRounded,
    currency,
    periodLabel,
    periodContextKey,
    revenueTrend,
    hotelCount,
    isLoading,
    isError,
    isReady,
  } = useAdminRevenueHotels(dateRange);

  useAdminTenantTableExport({
    tab: "revenue",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.revenue.selectDateRange",
          "Select a date range to view hotel revenue.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.revenue.loadError", "Failed to load revenue data.")}
      </p>
    );
  }

  return (
    <section className="space-y-6">
      <AdminRevenueHotelsTable rows={tableRows} loading={isLoading} />
      <div className="flex min-h-[511px] w-full flex-col gap-6 rounded-2xl border border-[#E5E5E5] bg-white p-4">
        {!isLoading ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <RevenueDistributionSection
              revenueDistribution={revenueDistribution}
              totalRevenueRounded={totalRevenueRounded}
              currency={currency}
              periodLabel={periodLabel}
              periodContextKey={periodContextKey}
              revenueTrend={revenueTrend}
            />
            <PartnerRevenueLegendSection
              revenueDistribution={revenueDistribution}
              totalRevenue={totalRevenue}
              hotelCount={hotelCount}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};
